import { TEMPORAL_RETENTION, UPLOAD_LIMITS } from '@famanage/schemas';
import { type ReactNode, useEffect } from 'react';
import { Link, type RouteObject } from 'react-router-dom';
import { t } from './i18n';

/**
 * The three public legal pages — /privacy, /terms, /legal — and the links that
 * reach them.
 *
 * All of it in one file on purpose. Until now these addresses fell through to
 * the `*` route, and because the site is served as an SPA with a 200 fallback
 * (apps/web/public/_redirects), famanage.com/privacy answered "This page does
 * not exist" with an HTTP 200 — a signup form collecting a name, an email and a
 * password behind a privacy policy that did not exist. The routes are exported
 * from here as plain route objects so router.tsx needs one import and one
 * spread, and nothing about how these pages are written can drift away from
 * where they are registered.
 *
 * The copy is this repository's own drafts — docs/legal/privacy-policy.md and
 * terms-of-service.md — rewritten for a screen. It is kept true rather than
 * generic: every data category named below was read out of the schema, every
 * provider named is one this code really sends data to, and every retention
 * period is the shipped default rather than an aspiration.
 *
 * Two things are deliberately absent, because the repository does not contain
 * them and inventing either would be worse than the gap: the operating
 * company's registered name and address, and a lawyer's review. Both are named
 * on the pages as missing, next to an address that reaches a person.
 * docs/legal/README.md is the checklist for closing them, and its bar — a
 * lawyer qualified in Israel and in the UAE — has not been met yet.
 */

/** The one contact address this repository actually has (Landing.tsx). */
const CONTACT = 'hello@famanage.com';

/** famanage is a product of KBR Global — the same registered entity behind kbr.global. */
const ENTITY = 'KBR Global Creative Consulting Ltd';
const ENTITY_ADDRESS = 'Suite 4.3.02, Block 4, Eurotowers, Gibraltar GX11 1AA';

/** Bump when the copy below changes in a way a reader would care about. */
const LAST_UPDATED = '15 August 2026';

/**
 * How long things are kept. The three below are the shipped defaults of the
 * retention worker (apps/api/src/env.ts: RETENTION_NOTIFICATION_DAYS,
 * RETENTION_AUDIT_LOG_DAYS, RETENTION_SOFT_DELETE_GRACE_DAYS) and of the backup
 * worker (BACKUP_RETENTION_DAYS), repeated here because apps/web cannot import
 * the API's env — so changing one means changing this page in the same commit.
 * Trash and research retention come from @famanage/schemas instead, and those
 * two cannot drift at all.
 */
const KEEP_DAYS = { notifications: 90, auditLog: 400, deleted: 30, backups: 30 } as const;

// ─── The documents ───────────────────────────────────────────────────────

/**
 * A paragraph, a bulleted list, a two-column table, or the boxed note that
 * carries what is still unfinished. `**like this**` is bold on screen — the
 * same markers the markdown drafts use, so the two stay diffable by eye.
 */
type Row = [string, string];

type Block =
  | string
  | { list: [string, ...string[]] }
  | { table: { columns: Row; rows: [Row, ...Row[]] } }
  | { note: string };

type Section = { id: string; heading: string; blocks: Block[] };

type Doc = { title: string; summary: string; sections: Section[] };

/** What both documents say about their own unfinished state. */
const STATUS_NOTE = `famanage is operated by ${ENTITY}, registered in Gibraltar, at ${ENTITY_ADDRESS}. One thing is still missing, and saying so is better than papering over it: this text has not yet been reviewed by a lawyer qualified in Israel or the UAE, the two countries the product is built for. Everything it says about how the software behaves is accurate and was written from the code itself. Until that review is done, ${CONTACT} reaches a person who will answer.`;

const PRIVACY: Doc = {
  title: 'Privacy Policy',
  summary:
    'What famanage records about a person, why, who else handles it, how long it is kept, and what you can ask us to do about it.',
  sections: [
    {
      id: 'who-this-is-for',
      heading: 'Who this is written for',
      blocks: [
        'famanage is software a household uses to run the work, the pay and the records of the people who work in their home.',
        'This policy has two readers, and both matter: the **household owner**, who signs up and pays; and the **worker** — a housekeeper, driver, nanny, cook, gardener or personal assistant — whose name, hours and wages are recorded here, and who did not choose this software.',
        `If you are a worker and anything below is unclear, you are entitled to a plain answer in your own language. Write to ${CONTACT} and ask for one.`,
        { note: STATUS_NOTE },
      ],
    },
    {
      id: 'who-is-responsible',
      heading: '1. Who is responsible for your information',
      blocks: [
        'The household that employs you decides what to record about you and why. In data-protection terms **the household owner is the controller and famanage is the processor**: they decide what goes into the record, we hold it and protect it.',
        {
          list: [
            'To correct or delete work records, hours or pay entries, ask the household owner — they are theirs to change.',
            `To ask what famanage itself holds, how it is secured, or to complain about how we handle it, write to ${CONTACT}.`,
            'If a household will not answer you, write to us anyway. We will not settle an employment dispute, but we will tell you what categories of information about you exist in the system, and help you use the rights further down this page.',
          ],
        },
      ],
    },
    {
      id: 'what-we-collect',
      heading: '2. What we collect',
      blocks: [
        'Everything in this section is what the software actually stores. None of it is speculative.',
        '**When you create an account** the form asks for your full name, your email address, a password, the name of your household (optional — without it the household is named after you), the country the household is in, and the language you want the app in.',
        'The password is stored only as an irreversible Argon2id hash. We never keep the password itself: nobody at famanage can read it, and nobody here can recover it for you.',
        '**As the account is used** we also hold a phone number where one is given, used for signing in and for WhatsApp messages; a profile photo, if one is uploaded; a two-factor secret, encrypted, if two-factor is turned on; and the time each person was last seen.',
        '**About a worker**, the household records: role and start date, employment status, an emergency contact, notes the owner writes, pay terms (type, rate, currency, overtime and holiday multipliers), work entries — days, hours, overtime, holidays, sick days, vacation and absences — payroll runs and payslips, deductions, advances, loans and repayments, end-of-service accrual, leave balances and requests, and cash requests and reimbursements.',
        '**Content made while using the product**: tasks and their history, with who did what and when; photographs offered as proof of finished work; voice recordings, where somebody records a note instead of typing it; photographs of receipts, including whatever is printed on them; messages between a household and the people who work in it, including over WhatsApp; and notifications.',
        '**Technical and security records**: an IP address, browser and device label for each session; an audit log of significant actions — sign-ins, money actions, permission changes — recording who acted and from where; and an error report if the app crashes.',
        '**When an AI feature runs** — reading a receipt, transcribing a voice note, translating, drafting a suggestion — we record which model ran, how many tokens it used, how long it took, and a hash of the input rather than the input itself.',
        'We never ask for information about anyone’s health, religion, politics or biometrics. Free text and photographs can carry that kind of detail by accident — a pharmacy receipt is the ordinary example — so keep out of notes and photographs anything that does not need to be there.',
      ],
    },
    {
      id: 'why',
      heading: '3. Why we use it',
      blocks: [
        {
          table: {
            columns: ['What for', 'On what basis'],
            rows: [
              ['Running the service the household signed up for', 'Performance of a contract'],
              [
                'Paying people correctly and keeping wage records',
                'The employer’s legal obligation, and our legitimate interest in the product doing its job',
              ],
              [
                'Security: sign-in, rate limiting, the audit log',
                'Legitimate interests, and legal obligation',
              ],
              ['Sending WhatsApp messages', 'Consent, recorded per person and withdrawable'],
              [
                'Optional AI features',
                'Legitimate interests, under a switch the household controls',
              ],
              [
                'Research and product improvement',
                'Separate, explicit, withdrawable consent — never bundled into signing up',
              ],
            ],
          },
        },
        '**We do not sell personal information, and we do not use it for advertising.**',
      ],
    },
    {
      id: 'who-else',
      heading: '4. Who else handles it',
      blocks: [
        'We use these providers, each only for the purpose named:',
        {
          table: {
            columns: ['Provider', 'What it handles'],
            rows: [
              ['Railway', 'Application hosting and the database'],
              [
                'Cloudflare',
                'Storage for photographs, voice recordings, receipts and backups, and some AI processing',
              ],
              ['OpenAI', 'Text and receipt-image processing for AI features'],
              ['Anthropic', 'Text processing for translation and assistance features'],
              ['Twilio', 'Sending and receiving WhatsApp messages'],
              ['Stripe', 'Subscription billing — card details go to Stripe and never to us'],
              ['Expo, with Apple and Google', 'Delivering push notifications to phones'],
              [
                'Google and Apple',
                'Optional “sign in with” — they confirm your email address to us',
              ],
            ],
          },
        },
        'Some of them sit outside Israel and outside the UAE: OpenAI and Anthropic process in the United States, and Cloudflare, Twilio, Stripe and the push services operate globally. Naming the transfer mechanism that covers this, where the law requires one, is part of the legal review still to come.',
        'A new provider is added to this list before it starts handling anything, not after. We will also disclose information where the law compels us, and we will tell the people affected unless we are forbidden from doing so.',
      ],
    },
    {
      id: 'retention',
      heading: '5. How long we keep it',
      blocks: [
        {
          list: [
            'An active account: for as long as the household uses the service.',
            `Notifications: about ${KEEP_DAYS.notifications} days after they are read.`,
            `The audit log: about ${KEEP_DAYS.auditLog} days. It outlives ordinary data deliberately — it is the record that answers who changed someone’s pay, and that question is asked long afterwards.`,
            `Anything deleted in the product: removed for good after a grace period of about ${KEEP_DAYS.deleted} days.`,
            `A file moved to Trash: ${UPLOAD_LIMITS.trashRetentionDays} days, and the exact date it will be destroyed is shown before you put it there.`,
            'Wage, payroll and end-of-service records: as long as employment law requires, which is usually longer than the periods above.',
            `If you agree to take part in research: a minimised phrase for ${TEMPORAL_RETENTION.minimizedPhraseDays} days and the structured record for ${TEMPORAL_RETENTION.structuredRecordDays} days. Withdrawing purges everything linked to you within ${TEMPORAL_RETENTION.withdrawalPurgeDays} days.`,
            `Database backups: about ${KEEP_DAYS.backups} days, then deleted.`,
            'Closing an account: a household can delete its data from inside the product, and deletion covers stored files as well as database rows. Aggregate counts that cannot identify anyone may remain.',
          ],
        },
      ],
    },
    {
      id: 'your-rights',
      heading: '6. What you can ask us to do',
      blocks: [
        'Whatever country you are in, you may ask us to:',
        {
          list: [
            'Tell you what we hold about you.',
            'Give you a copy. The product exports your own account’s data, and a household’s data, as a file you can keep.',
            'Correct anything that is wrong.',
            'Delete your information, subject to the wage-record duties above.',
            'Withdraw a consent — WhatsApp messages, research participation — at no cost, and with no loss of access to your own work and pay.',
            'Object to a particular use, or ask us to restrict it.',
          ],
        },
        `To use any of these, write to ${CONTACT}. We answer within 30 days, and there is no charge for asking.`,
        '**If you are in Israel**, you also have rights under the Privacy Protection Law, including inspection and correction, and you may complain to the Privacy Protection Authority. **If you are in the UAE**, Federal Decree-Law 45/2021 applies and you may complain to the UAE Data Office. **If you are in the EU or the UK**, the rights above apply under the GDPR and you may complain to your own supervisory authority.',
      ],
    },
    {
      id: 'security',
      heading: '7. How we protect it',
      blocks: [
        {
          list: [
            'Each household’s data is separated in the database itself rather than only by application code: a query outside a household’s context returns nothing, and the service refuses to start on a database connection that could bypass that.',
            'Passwords are hashed with Argon2id. Sign-in tokens last about fifteen minutes and rotate; reusing an old one ends the whole session family.',
            'Two-factor authentication is available to everyone, worker and owner alike, and its secret is encrypted where it is stored.',
            'Permissions are re-read from the database on every request, so removing one takes effect immediately rather than when a token expires.',
            'Traffic is encrypted in transit. Files and database backups are held in storage that encrypts them at rest.',
            'Sign-in attempts are rate limited per address and per account, and repeated failures are blocked.',
            'Access to production systems is restricted and logged.',
          ],
        },
        'No system is perfect. If a breach affects you, we will tell you and the relevant authority as the law requires.',
      ],
    },
    {
      id: 'children',
      heading: '8. Children',
      blocks: [
        'The service is for adults running a household. A household may record its own family members, including children, in its own records; that information stays under the household owner’s control and is used only to run their home.',
      ],
    },
    {
      id: 'changes',
      heading: '9. Changes to this policy',
      blocks: [
        'If we change this policy in a way that matters, we will tell account holders before the change takes effect.',
      ],
    },
    {
      id: 'contact',
      heading: '10. Contact',
      blocks: [`${ENTITY}, ${ENTITY_ADDRESS}. ${CONTACT}.`],
    },
  ],
};

const COOKIES: Doc = {
  title: 'Cookies and Local Storage',
  summary:
    'famanage does not use cookies. This page says exactly what is stored in your browser instead, and why.',
  sections: [
    {
      id: 'no-cookies',
      heading: 'We do not use cookies',
      blocks: [
        'famanage sets no cookies — not for tracking, not for advertising, not for analytics. There is no cookie banner because there is nothing to ask permission for: everything below is strictly necessary for the product to work, the same legal category a cookie banner is never required for.',
      ],
    },
    {
      id: 'local-storage',
      heading: 'What is stored in your browser',
      blocks: [
        "Instead of cookies, the app keeps a few values in your browser's local storage — data that stays on your device and is never sent anywhere except back to famanage's own servers when you use the product.",
        {
          list: [
            'Your sign-in session, so you are not asked to log in again on every visit.',
            'Your role (owner or worker) and permissions, so the right screens show.',
            'Your chosen language and light/dark theme.',
            'Tasks and updates queued while you were offline, until they can sync.',
          ],
        },
        "Clearing your browser's site data for famanage removes all of it and signs you out.",
      ],
    },
    {
      id: 'no-third-party',
      heading: 'No third-party trackers',
      blocks: [
        'famanage does not load Google Analytics, Meta Pixel, or any other third-party tracking or advertising script. A small first-party counter records which pages are visited, entirely inside your own browser session, with no cookie and nothing that identifies you across visits or devices.',
        { note: STATUS_NOTE },
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      blocks: [`${ENTITY}, ${ENTITY_ADDRESS}. ${CONTACT}.`],
    },
  ],
};

const TERMS: Doc = {
  title: 'Terms of Service',
  summary: 'The agreement between famanage and the person who creates a household account.',
  sections: [
    {
      id: 'the-agreement',
      heading: 'The agreement',
      blocks: [
        'These terms are between famanage and the person who creates a household account. Creating an account accepts them.',
        { note: STATUS_NOTE },
      ],
    },
    {
      id: 'what-the-service-is',
      heading: '1. What the service is',
      blocks: [
        'famanage is software for running a household: handing out work, tracking hours, working out pay, recording receipts, and keeping the records that go with employing people at home.',
        '**famanage is a record-keeping and coordination tool. It is not an employer, not a payroll bureau, not an accountant and not a legal adviser.** We do not pay anyone on your behalf, we do not file anything with any authority, and we do not check that what you enter is lawful or correct.',
      ],
    },
    {
      id: 'you-are-the-employer',
      heading: '2. You are the employer',
      blocks: [
        'If you use famanage to manage people who work in your home then **you** are their employer, with every duty that carries in your country: lawful wages, hours, rest, leave, end-of-service payments, permits, insurance, and keeping proper records.',
        'The product works figures out of what you enter — overtime, deductions, leave balances, end-of-service accrual. Those calculations are a convenience, not legal or accounting advice, and they depend entirely on the terms you configured. **Check them before you pay anyone.** If a figure looks wrong it probably is, and the payment is yours either way.',
      ],
    },
    {
      id: 'the-people-who-work-for-you',
      heading: '3. The people who work for you',
      blocks: [
        'Workers get their own accounts. These are not optional:',
        {
          list: [
            '**Tell them.** Before you record someone in famanage, tell them the software exists, what it records about them, and that they can see their own records in it.',
            '**Do not use it to harm them.** No surveillance beyond what the work honestly requires, no recording your country’s law forbids, and no using the audit log or the record of where work happened as a way to intimidate anyone.',
            '**Consent means consent.** WhatsApp messaging and research participation are opt-in per person, and withdrawing must cost them nothing.',
            '**Their records are theirs to see.** Do not disable someone’s account to keep them away from their own pay history.',
          ],
        },
        'We may suspend an account we reasonably believe is being used to exploit or endanger a worker. That is a deliberate limit on this product’s usefulness, and we accept what it costs.',
      ],
    },
    {
      id: 'your-account',
      heading: '4. Your account',
      blocks: [
        {
          list: [
            'Keep your credentials to yourself. You are responsible for what happens under your account.',
            'Give accurate information.',
            'One household per account unless we agree otherwise.',
            'You must be legally an adult.',
            'Co-owners you invite get owner-level access to that household’s data, wages included. Invite deliberately.',
          ],
        },
      ],
    },
    {
      id: 'payment',
      heading: '5. Payment',
      blocks: [
        {
          list: [
            'Price, currency and billing period are whatever is shown at checkout.',
            'Card details go to our payment provider and never to us.',
            'Fees are charged in advance and are not refundable except where the law says otherwise.',
            'If a payment fails we will tell you and give you a fair chance to fix it before restricting the account.',
            'We may change prices with at least 30 days’ notice, and the change applies from your next billing period.',
            'You can cancel at any time; the subscription runs to the end of the period you have paid for.',
          ],
        },
      ],
    },
    {
      id: 'your-data',
      heading: '6. Your data',
      blocks: [
        'You own what you put in. We hold it to run the service, under the Privacy Policy.',
        'You can export your household’s data from inside the product at any time, including after cancelling and before deletion.',
        'If you delete your household we delete its data, stored files included, after a short grace period. Some records may be kept where the law requires it.',
      ],
    },
    {
      id: 'availability',
      heading: '7. Availability',
      blocks: [
        'We aim to keep the service running, and to keep backups, but we do not promise uninterrupted availability. We may take it down for maintenance, and will give notice where we reasonably can.',
      ],
    },
    {
      id: 'acceptable-use',
      heading: '8. Acceptable use',
      blocks: [
        'Do not break the law with it, upload malicious code, try to reach another household’s data, scrape, resell or reverse-engineer the service, deliberately overload it, or use it to harass anyone.',
      ],
    },
    {
      id: 'ai-features',
      heading: '9. AI features',
      blocks: [
        'Some features use third-party AI models — reading receipts, transcribing voice, translating, suggesting tasks. Their output can be wrong. **Anything an AI feature produces that affects someone’s pay must be checked by a person before it becomes a payment.** AI features can be switched off for a household.',
      ],
    },
    {
      id: 'liability',
      heading: '10. Liability',
      blocks: [
        'To the maximum extent the law allows:',
        {
          list: [
            'The service is provided as it is.',
            'We are not liable for indirect or consequential loss, or for lost profit or lost data.',
            'Our total liability in any twelve-month period is capped at what you paid us in that period.',
            '**Nothing here limits liability that cannot lawfully be limited**, including death or personal injury caused by negligence, and fraud.',
            'We are not liable for your employment-law obligations, for what you pay or fail to pay, or for a figure you approved without checking.',
          ],
        },
      ],
    },
    {
      id: 'ending',
      heading: '11. Ending the agreement',
      blocks: [
        'You may stop at any time. We may suspend or end an account for a material breach of these terms, with notice and a chance to put it right — unless the breach involves harm to a worker or a security threat, where we may act immediately.',
      ],
    },
    {
      id: 'changes',
      heading: '12. Changes to these terms',
      blocks: [
        'We will give at least 30 days’ notice of a material change. Continuing to use the service after it takes effect accepts it. If you would rather not, you may cancel and have the unused period refunded.',
      ],
    },
    {
      id: 'law',
      heading: '13. Law and disputes',
      blocks: [
        'Which country’s law governs these terms, and whose courts hear a dispute, is part of the legal review still to come and is deliberately not stated here rather than guessed at.',
        'Whatever is settled, it will not remove a protection that the mandatory law of your own country of residence gives you.',
      ],
    },
    {
      id: 'contact',
      heading: '14. Contact',
      blocks: [`${ENTITY}, ${ENTITY_ADDRESS}. ${CONTACT}.`],
    },
  ],
};

// ─── Rendering ───────────────────────────────────────────────────────────

/** `**like this**` becomes bold. Odd positions are the captured groups. */
function emphasize(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: the split of one fixed copy string — position IS the identity, and the parts never reorder
      <strong key={i} className="font-semibold text-foreground">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

/** Enough of a key to be stable without inventing ids for static prose. */
function blockKey(block: Block): string {
  if (typeof block === 'string') return block;
  if ('list' in block) return block.list[0];
  if ('note' in block) return block.note;
  return block.table.rows[0][0];
}

function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((block) => {
        const key = blockKey(block);
        if (typeof block === 'string') {
          return (
            <p key={key} className="mt-4 text-[15px] leading-7 text-muted-foreground">
              {emphasize(block)}
            </p>
          );
        }
        if ('note' in block) {
          return (
            <div
              key={key}
              className="mt-5 rounded-card border border-border bg-muted p-5 text-[14px] leading-6 text-muted-foreground"
            >
              <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                {t('Still unfinished')}
              </p>
              {emphasize(block.note)}
            </div>
          );
        }
        if ('list' in block) {
          return (
            <ul key={key} className="mt-4 space-y-2.5">
              {block.list.map((item) => (
                <li key={item} className="flex gap-3 text-[15px] leading-7 text-muted-foreground">
                  <span
                    aria-hidden
                    className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-border"
                  />
                  <span>{emphasize(item)}</span>
                </li>
              ))}
            </ul>
          );
        }
        const { columns, rows } = block.table;
        return (
          <div key={key} className="mt-5 overflow-hidden rounded-card border border-border">
            <table className="w-full border-collapse text-start text-[14px]">
              <thead>
                <tr className="bg-muted">
                  {columns.map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className="px-4 py-3 text-start text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row[0]} className="border-t border-border align-top">
                    {row.map((cell, column) => (
                      <td
                        key={columns[column]}
                        className={
                          column === 0
                            ? 'px-4 py-3 font-semibold text-foreground'
                            : 'px-4 py-3 leading-6 text-muted-foreground'
                        }
                      >
                        {emphasize(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

/**
 * The frame all three pages share: the name as the way back, the document, and
 * the other two documents underneath it.
 *
 * `dir="ltr"` on the body and not on the shell, because the copy is English
 * only for now — a Hebrew or Urdu reader gets the app's own direction in the
 * chrome and correctly aligned English in the text. Making these pages readable
 * in Urdu and Tagalog is a real gap, and docs/legal/README.md already names it.
 */
function LegalShell({
  title,
  summary,
  updated = true,
  children,
}: {
  title: string;
  summary: string;
  updated?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · famanage`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="app-shell">
      <header className="border-b border-border px-6 py-5">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
          <Link
            to="/"
            className="text-base font-bold tracking-tight text-foreground"
            dir="ltr"
            aria-label={t('Back to home')}
          >
            famanage
          </Link>
          <Link
            to="/legal"
            className="text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            {t('Legal')}
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12" dir="ltr">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          {title}
        </h1>
        <p className="mt-3 text-[15px] leading-7 text-muted-foreground">{summary}</p>
        {updated && (
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
            Last updated {LAST_UPDATED}
          </p>
        )}
        {children}
      </main>

      <footer className="border-t border-border px-6 py-8">
        <div
          className="mx-auto flex max-w-3xl flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-muted-foreground"
          dir="ltr"
        >
          <LegalLinks />
          <a href={`mailto:${CONTACT}`} className="hover:text-foreground">
            {CONTACT}
          </a>
        </div>
      </footer>
    </div>
  );
}

function LegalDoc({ doc }: { doc: Doc }) {
  return (
    <LegalShell title={doc.title} summary={doc.summary}>
      {doc.sections.map((section) => (
        <section key={section.id} id={section.id} className="mt-10 scroll-mt-6">
          <h2 className="font-heading text-xl font-bold tracking-tight text-foreground">
            {section.heading}
          </h2>
          <Blocks blocks={section.blocks} />
        </section>
      ))}
    </LegalShell>
  );
}

/** /legal — the way in, for anyone who guessed at the address. */
function LegalIndex() {
  const docs = [
    {
      to: '/privacy',
      title: PRIVACY.title,
      summary: PRIVACY.summary,
    },
    {
      to: '/terms',
      title: TERMS.title,
      summary: TERMS.summary,
    },
    {
      to: '/cookies',
      title: COOKIES.title,
      summary: COOKIES.summary,
    },
  ];
  return (
    <LegalShell
      title="Legal"
      summary="The documents that govern using famanage, and how to reach us about any of them."
      updated={false}
    >
      <div className="mt-8 space-y-4">
        {docs.map((doc) => (
          <Link
            key={doc.to}
            to={doc.to}
            className="block rounded-card border border-border bg-card p-6 transition hover:border-primary/40"
          >
            <p className="font-heading text-lg font-bold tracking-tight text-foreground">
              {doc.title}
            </p>
            <p className="mt-2 text-[15px] leading-7 text-muted-foreground">{doc.summary}</p>
          </Link>
        ))}
      </div>
      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold tracking-tight text-foreground">
          Data processing, on paper
        </h2>
        <Blocks
          blocks={[
            `A Data Processing Addendum — the controller and processor terms, the list of providers who handle household data, and the security measures behind it — is drafted for households that need one on paper. Ask at ${CONTACT}.`,
            { note: STATUS_NOTE },
          ]}
        />
      </section>
    </LegalShell>
  );
}

// ─── What the rest of the app uses ───────────────────────────────────────

/**
 * The legal links a public page carries. Bare links, so a footer can drop them
 * into whatever row it already has — which is how the landing page uses them.
 */
export function LegalLinks({
  className = 'hover:text-foreground',
}: {
  className?: string;
}) {
  return (
    <>
      <Link to="/privacy" className={className}>
        {t('Privacy')}
      </Link>
      <Link to="/terms" className={className}>
        {t('Terms')}
      </Link>
      <Link to="/cookies" className={className}>
        {t('Cookies')}
      </Link>
      <Link to="/legal" className={className}>
        {t('Legal')}
      </Link>
    </>
  );
}

/**
 * The line under a signup button. A whole sentence per language rather than
 * one spliced around the links, so word order survives translation.
 */
export function LegalConsent() {
  return (
    <p className="mt-4 text-center text-[13px] leading-6 text-muted-foreground">
      {t('Creating a household accepts these:')}{' '}
      {/* One unit, so a narrow card breaks before the links rather than
          between them and leaves the separator dangling. */}
      <span className="whitespace-nowrap">
        <Link to="/terms" className="font-semibold text-foreground underline underline-offset-2">
          {t('Terms')}
        </Link>
        {' · '}
        <Link to="/privacy" className="font-semibold text-foreground underline underline-offset-2">
          {t('Privacy')}
        </Link>
      </span>
    </p>
  );
}

/**
 * The three routes, exported as plain route objects so registering them costs
 * router.tsx one import and one spread. Public and unguarded on purpose: a
 * worker deciding whether to trust this software, and an app store reviewer
 * checking that the privacy policy resolves, both arrive without an account.
 */
export const legalRoutes: RouteObject[] = [
  { path: '/privacy', element: <LegalDoc doc={PRIVACY} /> },
  { path: '/terms', element: <LegalDoc doc={TERMS} /> },
  { path: '/cookies', element: <LegalDoc doc={COOKIES} /> },
  { path: '/legal', element: <LegalIndex /> },
];
